import { USDC_BASE } from "../../../anyspend/constants/index.js";
import { anyspendService } from "../../../anyspend/services/anyspend.js";
import { buildMetadata, buildPayload, normalizeAddress } from "../../../anyspend/utils/index.js";
import { useB3Config } from "../../../global-account/react/index.js";
import { useVisitorData } from "@fingerprintjs/fingerprintjs-pro-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { parseUnits } from "viem";
import { base } from "viem/chains";
import { getCachedWalletHeaders, useWalletAuthHeaders } from "./useKycStatus.js";
import { useValidatedClientReferenceId } from "./useValidatedClientReferenceId.js";
/**
 * Hook for creating onramp orders in the Anyspend protocol
 * Specifically handles orders that involve fiat-to-crypto onramp functionality
 */
export function useAnyspendCreateOnrampOrder({
  onSuccess,
  onError
} = {}) {
  // Get B3 context values
  const {
    partnerId
  } = useB3Config();
  const {
    address,
    getHeaders: getWalletAuthHeaders
  } = useWalletAuthHeaders();
  // Get validated client reference ID from B3 context
  const createValidatedClientReferenceId = useValidatedClientReferenceId();
  // Get fingerprint data
  const {
    data: fpData
  } = useVisitorData({
    extendedResult: true
  }, {
    immediate: true
  });
  const visitorData = fpData && {
    requestId: fpData.requestId,
    visitorId: fpData.visitorId
  };
  const {
    mutate: createOrder,
    isPending
  } = useMutation({
    mutationFn: async params => {
      const {
        recipientAddress,
        orderType,
        dstChain,
        dstToken,
        srcFiatAmount,
        onramp,
        creatorAddress,
        expectedDstAmount,
        nft,
        tournament,
        payload
      } = params;
      const clientReferenceId = await createValidatedClientReferenceId(params);
      try {
        // Validate required onramp fields
        if (!onramp.vendor || !onramp.country) {
          throw new Error("Missing required onramp fields: vendor, country");
        }
        const srcToken = USDC_BASE;
        const srcChain = base.id;
        // Create order with USDC on Base as source
        const srcAmountOnRampInWei = parseUnits(srcFiatAmount, USDC_BASE.decimals);
        // For card payments, include wallet auth headers so the backend can verify
        // KYC by the signing wallet address (may differ from the B3 JWT address).
        // First try cached headers (from KycGate), then sign on-the-fly.
        // The user is already clicking "Continue"/"Pay" so signing here is acceptable.
        let kycWalletHeaders;
        if (onramp.vendor === "stripe-web2" && address) {
          kycWalletHeaders = getCachedWalletHeaders(address) || (await getWalletAuthHeaders());
        }
        return await anyspendService.createOrder({
          recipientAddress: normalizeAddress(recipientAddress),
          type: orderType,
          srcChain,
          srcTokenAddress: normalizeAddress(srcToken.address),
          dstChain,
          dstTokenAddress: normalizeAddress(dstToken.address),
          srcAmount: srcAmountOnRampInWei.toString(),
          payload: buildPayload(orderType, {
            orderType,
            srcToken,
            dstToken,
            expectedDstAmount,
            nft,
            tournament,
            payload: {
              ...payload
            }
          }),
          onramp,
          metadata: buildMetadata(orderType, {
            orderType,
            srcToken,
            dstToken,
            expectedDstAmount,
            nft,
            tournament,
            payload: {
              ...payload
            }
          }),
          creatorAddress: creatorAddress ? normalizeAddress(creatorAddress) : undefined,
          partnerId,
          clientReferenceId,
          visitorData,
          callbackMetadata: params.callbackMetadata,
          feeOnTop: params.feeOnTop,
          kycWalletHeaders
        });
      } catch (error) {
        // If the error has a response with message and statusCode, throw that
        if (error?.data) {
          throw error.data;
        }
        // Otherwise throw the original error
        throw error;
      }
    },
    onSuccess: data => {
      onSuccess?.(data);
    },
    onError: error_0 => {
      onError?.(error_0);
    }
  });
  return useMemo(() => ({
    createOrder,
    isCreatingOrder: isPending
  }), [createOrder, isPending]);
}